import { XMarkIcon } from './icons'

type InfoBoxVariant = 'info' | 'warning' | 'success' | 'error'

interface InfoBoxProps {
  variant: InfoBoxVariant
  title?: string
  message: string
  action?: {
    label: string
    onClick: () => void
  }
  dismissible?: boolean
  onDismiss?: () => void
}

const variantConfig: Record<
  InfoBoxVariant,
  { bg: string; border: string; icon: string; text: string; iconComponent: React.ReactNode }
> = {
  info: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    icon: 'text-sky-600',
    text: 'text-sky-800',
    iconComponent: (
      <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-600',
    text: 'text-amber-800',
    iconComponent: (
      <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
  },
  success: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: 'text-emerald-600',
    text: 'text-emerald-800',
    iconComponent: (
      <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    text: 'text-red-800',
    iconComponent: (
      <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
}

export function InfoBox({
  variant,
  title,
  message,
  action,
  dismissible = false,
  onDismiss,
}: InfoBoxProps) {
  const config = variantConfig[variant]

  return (
    <div
      role="alert"
      className={`
        relative flex gap-3 p-4 rounded-xl border
        ${config.bg} ${config.border}
      `}
    >
      <div className={`shrink-0 ${config.icon}`}>{config.iconComponent}</div>
      
      <div className="flex-1 min-w-0">
        {title && (
          <h4 className={`font-semibold mb-1 ${config.text}`}>{title}</h4>
        )}
        <p className={`text-sm/6 ${config.text}`}>{message}</p>
        
        {action && (
          <button
            onClick={action.onClick}
            className={`
              mt-2 text-sm/5 font-medium underline underline-offset-2
              ${config.text} hover:no-underline
            `}
          >
            {action.label} →
          </button>
        )}
      </div>

      {dismissible && onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className={`
            shrink-0 p-1 -m-1 rounded-lg
            ${config.text} opacity-60 hover:opacity-100
            transition-opacity
          `}
        >
          <XMarkIcon className="size-5" />
        </button>
      )}
    </div>
  )
}
